import { BodyText, Skeleton } from "@/components/ui";

import { cn } from "@/lib/utils";
import { useS3Download } from "@/network/useS3Download";
import { useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import type * as fhirTypes from "fhir/r4";
import { CircleSlash, File } from "lucide-react";

interface FilePreviewProps {
  media: fhirTypes.DocumentReference;
}

export function FilePreview({ media }: FilePreviewProps) {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data, isLoading, isError } = useS3Download({
    patientId: patientId as string,
    path: media.content?.[0]?.attachment?.url || "",
    enabled: Boolean(media.content?.[0]?.attachment?.url),
  });

  const date = new Date(media.content[0].attachment.creation || "");
  const imageClassName =
    "h-20 w-20 rounded-md object-cover flex-shrink-0 flex justify-center items-center bg-secondary text-primary";

  if (!data || isLoading)
    return (
      <div className="flex items-center gap-2">
        <Skeleton className={cn(imageClassName, "bg-slate-300 text-primary")} />
        <div className="flex flex-col w-full overflow-hidden gap-1">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
    );

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <div className={cn(imageClassName, "bg-error text-error")}>
          <CircleSlash size="18px" />
        </div>
        <BodyText tag="p" size="sm" textColor="error">
          Something went wrong
        </BodyText>
      </div>
    );
  }

  return (
    <a
      href={URL.createObjectURL(data)}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2"
    >
      {data.type === "application/pdf" ? (
        <div className={imageClassName}>
          <File size="18px" />
        </div>
      ) : (
        <img
          className={imageClassName}
          src={URL.createObjectURL(data)}
          alt="encounter preview file"
        />
      )}

      <div className="flex flex-col w-full overflow-hidden gap-1">
        {media.content[0].attachment.title && (
          <BodyText tag="p" size="sm" textColor="primary">
            {media.content[0].attachment.title}
          </BodyText>
        )}
        <BodyText tag="p" size="sm" textColor="tertiary">
          {format(date, "M/d/yyyy")} at {format(date, "h:mma")}
        </BodyText>
      </div>
    </a>
  );
}
